/**
 * 타겟 키워드 캠페인의 **차례와 주소** — 사이드바 하위 줄과 추적 관리 카드가 같은 캠페인을 가리키게 한다.
 *
 * 🔑 차례는 `createdAt` 오름차순이다 — 사이드바가 이 차례로 줄을 세우므로, 「첫 캠페인」은 늘
 *   사이드바 맨 위 줄과 같다.
 */

import { paths } from 'src/routes/paths';

/**
 * 캠페인 문서의 `createdAt` 을 밀리초로 읽는다(Timestamp · `{ seconds }` · 문자열 모두).
 * @returns 읽을 수 없으면 0
 */
export function getCampaignCreatedAtMs(campaign: Record<string, any> | null | undefined): number {
  const value = campaign?.createdAt;
  if (!value) return 0;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/** `createdAt` 오름차순 사본 — 원본 배열은 건드리지 않는다 */
export function sortKeywordCampaignsByCreatedAt<T extends Record<string, any>>(
  campaigns: readonly T[] | null | undefined,
): T[] {
  return [...(Array.isArray(campaigns) ? campaigns : [])].sort(
    (a, b) => getCampaignCreatedAtMs(a) - getCampaignCreatedAtMs(b),
  );
}

/** 캠페인 하나의 타겟 키워드 화면 주소 — `/service/tracking/target-mentions/{id}` */
export function toKeywordMonitoringHref(campaignId: string): string {
  return `${paths.service.keywordMonitoring}/${encodeURIComponent(campaignId)}`;
}

/**
 * 첫 캠페인(사이드바 맨 위 줄)의 화면 주소.
 * @returns 캠페인이 없거나 id 가 비면 `null`
 */
export function firstKeywordCampaignHref(
  campaigns: readonly Record<string, any>[] | null | undefined,
): string | null {
  const first = sortKeywordCampaignsByCreatedAt(campaigns)
    .find((campaign) => String(campaign?.id ?? '').trim() !== '');
  return first ? toKeywordMonitoringHref(String(first.id).trim()) : null;
}
